import React, { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout';
import { 
  Search, 
  Plus, 
  Filter,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  RotateCcw,
  ChevronsUpDown,
  Copy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import OfferForm from '../../components/OfferForm';
import TablePagination from '../../components/TablePagination';

function Index({ offers, pagination, filters, filter_options, can_edit, sorting }) {
  const [search, setSearch] = useState(filters.search || '');
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '');
  const [selectedCountry, setSelectedCountry] = useState(filters.country || '');
  const [selectedProvider, setSelectedProvider] = useState(filters.provider_id || '');
  const [selectedClient, setSelectedClient] = useState(filters.client_id || '');
  const [selectedVertical, setSelectedVertical] = useState(filters.vertical_id || '');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    vertical_id: '',
    country_id: '',
    status: 'active',
    provider_id: '',
    client_id: ''
  });

  const handleFilter = (page = 1, sort = sorting?.column, direction = sorting?.direction) => {
    router.get('/offers', {
      search: search,
      status: selectedStatus,
      country: selectedCountry,
      provider_id: selectedProvider,
      client_id: selectedClient,
      vertical_id: selectedVertical,
      sort: sort || '',
      direction: sort ? direction || 'asc' : '',
      page: page
    }, {
      preserveState: true,
      preserveScroll: true
    });
  };

  // First click sorts ascending, clicking the active column flips it
  const handleSort = (column: string) => {
    const nextDirection =
      sorting?.column === column && sorting?.direction === 'asc' ? 'desc' : 'asc';
    handleFilter(1, column, nextDirection);
  };

  const SortableHeader = ({ column, label }: { column: string; label: string }) => {
    const active = sorting?.column === column;
    return (
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <button
          type="button"
          onClick={() => handleSort(column)}
          aria-sort={active ? (sorting.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
          className={`group inline-flex items-center gap-1 uppercase tracking-wider cursor-pointer hover:text-gray-700 ${
            active ? 'text-gray-900' : 'text-gray-500'
          }`}
        >
          {label}
          {active ? (
            sorting.direction === 'asc' ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )
          ) : (
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </button>
      </th>
    );
  };

  const handleReset = () => {
    setSearch('');
    setSelectedStatus('');
    setSelectedCountry('');
    setSelectedProvider('');
    setSelectedClient('');
    setSelectedVertical('');
    router.get('/offers', {}, { preserveState: true });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    router.post('/offers', { offer: formData }, {
      onSuccess: () => {
        setIsDialogOpen(false);
        resetForm();
      }
    });
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      name: offer.name,
      description: offer.description || '',
      vertical_id: filter_options.verticals.find(v => v.name === offer.vertical)?.id || '',
      country_id: filter_options.countries.find(c => c.name === offer.country)?.id || '',
      status: offer.status,
      provider_id: filter_options.providers.find(p => p.name === offer.provider)?.id || '',
      client_id: filter_options.clients.find(c => c.name === offer.client)?.id || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    const isConfirmed = window.confirm(
      `Are you sure you want to update the offer "${formData.name}"?`
    );
    
    if (isConfirmed) {
      router.put(`/offers/${editingOffer.id}`, { offer: formData }, {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingOffer(null);
          resetForm();
        }
      });
    }
  };

  const handleDelete = (offer) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the offer "${offer.name}"?\n\nThis action cannot be undone.`
    );
    
    if (isConfirmed) {
      router.delete(`/offers/${offer.id}`, {
        onSuccess: () => {
          // Pagination will be handled automatically by the controller
        }
      });
    }
  };
  const handleDuplicate = (offer) =>{
    if (!confirm(`Duplicate "${offer.name}" with all of its subject lines, from lines, creatives, footers and links?`)) return;
    router.post(`/offers/${offer.id}/duplicate`, {}, { preserveScroll: true });
  }
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      vertical_id: '',
      country_id: '',
      status: 'active',
      provider_id: '',
      client_id: ''
    });
  };

  return (
    <>
      <Head title="Offers | Mailkit - Resolute" />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Offers</h1>
              <p className="mt-2 text-sm text-gray-600">Manage and track all your offers in one place</p>
            </div>
            {can_edit && (
              <div className="mt-4 sm:mt-0">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer">
                      <Plus className="w-4 h-4 mr-1" />
                      Add New Offer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Add New Offer</DialogTitle>
                      <DialogDescription>
                        Create a new offer for your clients.
                      </DialogDescription>
                    </DialogHeader>
                    <OfferForm
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleFormSubmit}
                      onCancel={() => setIsDialogOpen(false)}
                      filterOptions={filter_options}
                      submitText="Create Offer"
                    />
                  </DialogContent>
                </Dialog>

                {/* Edit Offer Dialog */}
                <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Edit Offer</DialogTitle>
                      <DialogDescription>
                        Update the offer details.
                      </DialogDescription>
                    </DialogHeader>
                    <OfferForm
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleEditSubmit}
                      onCancel={() => setIsEditDialogOpen(false)}
                      filterOptions={filter_options}
                      submitText="Update Offer"
                      isEditing={true}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl border border-gray-200 mb-4">
          <div className="px-4 py-3">
            <button 
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <Filter className="w-4 h-4 mr-2 text-gray-500" />
                <h3 className="text-base font-semibold text-gray-900">Filters</h3>
              </div>
              {filtersExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>
          </div>
          
          {filtersExpanded && (
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Offer Name</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="pl-10 w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search by name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    {filter_options.statuses.map(status => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    {filter_options.countries.map(country => (
                      <SelectItem key={country.id} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue placeholder="All Providers" />
                  </SelectTrigger>
                  <SelectContent>
                    {filter_options.providers.map(provider => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent>
                    {filter_options.clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vertical</label>
                <Select value={selectedVertical} onValueChange={setSelectedVertical}>
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue placeholder="All Verticals" />
                  </SelectTrigger>
                  <SelectContent>
                    {filter_options.verticals.map(vertical => (
                      <SelectItem key={vertical.id} value={vertical.id}>
                        {vertical.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
              <div className="mt-4 flex gap-2">
                <Button 
                  onClick={() => handleFilter()}
                  className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-sm px-3 py-2 cursor-pointer"
                >
                  <Filter className="w-3 h-3 mr-1" />
                  Apply Filters
                </Button>
                <Button 
                  onClick={handleReset}
                  variant="outline"
                  className="border-gray-300 hover:bg-gray-50 text-sm px-3 py-2 cursor-pointer"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        {pagination.total_count > 0 && (
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(pagination.current_page * pagination.per_page, pagination.total_count)}</span> of{' '}
              <span className="font-semibold">{pagination.total_count}</span> offers
              {pagination.total_pages > 1 && (
                <span className="ml-2">
                  (Page {pagination.current_page} of {pagination.total_pages})
                </span>
              )}
            </p>
          </div>
        )}

        {/* Offers Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader column="name" label="Name" />
                  <SortableHeader column="client" label="Client" />
                  <SortableHeader column="status" label="Status" />
                  <SortableHeader column="vertical" label="Vertical" />
                  <SortableHeader column="country" label="Country" />
                  <SortableHeader column="provider" label="Provider" />
                  {can_edit && (
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {offers.length === 0 ? (
                  <tr>
                    <td colSpan={can_edit ? 7 : 6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="rounded-full bg-gray-100 p-3 mb-4">
                          <Search className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">No offers found</p>
                        <p className="text-gray-400 text-xs mt-1">Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  offers.map((offer) => (
                    <tr key={offer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-2 whitespace-nowrap">
                        <Link 
                          href={`/offers/${offer.id}`} 
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {offer.name}
                        </Link>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                        {offer.client}
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            offer.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : offer.status === 'inactive'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            offer.status === 'active'
                              ? 'bg-green-600'
                              : offer.status === 'inactive'
                              ? 'bg-red-600'
                              : 'bg-yellow-600'
                          }`} />
                          {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                        {offer.vertical}
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                        {offer.country}
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                        {offer.provider}
                      </td>
                      {can_edit && (
                        <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(offer)}
                              className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDuplicate(offer)}
                              className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDelete(offer)}
                              className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination -- handleFilter carries the search, filters and sort along */}
        <TablePagination
          pagination={pagination}
          label="results"
          onPageChange={(page) => handleFilter(page)}
        />
      </div>
    </div>
    </>
  );
}

// Set persistent layout
Index.layout = page => <Layout>{page}</Layout>;

export default Index;